<script setup>
  import useVuelidate from '@vuelidate/core'

  import { computed } from 'vue'

  import FieldRendererLabel from '@/features/settingsManagement/fieldManagement/edit/plugin/FieldRendererLabel.vue'
  import FieldRendererValidationMessages from '@/features/settingsManagement/fieldManagement/edit/plugin/FieldRendererValidationMessages.vue'
  import FieldRendererWrapper from '@/features/settingsManagement/fieldManagement/edit/plugin/FieldRendererWrapper.vue'
  import { useFieldPlugins } from '@/features/settingsManagement/fieldManagement/edit/plugin/fieldsPlugin.js'
  import {
    fieldCommonProps,
    normalizeFieldExtraData,
  } from '@/features/settingsManagement/fieldManagement/edit/utils/fieldUtils.js'
  import { mergeDeep } from '@/features/utils/object.utils.js'

  defineEmits(['update:modelValue'])

  const props = defineProps({
    ...fieldCommonProps,
  })

  const { getPlugin } = useFieldPlugins()
  const plugin = getPlugin('select')

  const extraData = computed(() => {
    return mergeDeep(plugin.defaultExtraData, normalizeFieldExtraData(props.field.extraData))
  })

  const placeholder = computed(() => {
    return extraData.value.placeholder || 'Select...'
  })

  const rules = computed(() => ({
    value: plugin.getValidations({ props }),
  }))

  const state = computed(() => ({
    value: props.modelValue,
  }))

  const v$ = useVuelidate(rules, state)

  async function validate() {
    const result = await v$.value.$validate()
    return {
      isValid: result,
      errors: Object.entries(v$.value.$errors).map(([, error]) => error.$message),
    }
  }

  const options = computed(() => {
    return extraData.value.options || []
  })

  defineExpose({
    validate,
  })
</script>

<template>
  <FieldRendererWrapper :field="field">
    <template #label>
      <FieldRendererLabel :field="field" :external-required="externalRequired" />
    </template>

    <MultiSelect
      :placeholder="placeholder"
      :value="modelValue"
      :options="options"
      :disabled="disabled"
      :invalid="v$.value.$error || hasExternalError"
      :option-label="option => option.label"
      :option-value="option => option.value"
      display="chip"
      :filter="true"
      fluid
      @change="$emit('update:modelValue', $event.value)"
    />

    <template #validation>
      <FieldRendererValidationMessages v-if="v$.value.$error" :error-messages="v$.value.$errors" />
    </template>
  </FieldRendererWrapper>
</template>
